import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { ca } from './locales/ca';
import { en } from './locales/en';
import { es } from './locales/es';
import { fr } from './locales/fr';
import { de } from './locales/de';
import { it } from './locales/it';
import { eu } from './locales/eu';
import { gl } from './locales/gl';
import { zh } from './locales/zh';
import { vi } from './locales/vi';
import { ja } from './locales/ja';
import { ko } from './locales/ko';
import { ru } from './locales/ru';
import { ro } from './locales/ro';
import { ar } from './locales/ar';
import { tr } from './locales/tr';
import { pl } from './locales/pl';
import { ms } from './locales/ms';
import { ta } from './locales/ta';
import { api } from '../lib/api';

const BUILTIN = { ca, en, es, fr, de, it, eu, gl, zh, vi, ja, ko, ru, ro, ar, tr, pl, ms, ta };
const BUILTIN_NAMES = {
  ca: 'Català', en: 'English', es: 'Español', fr: 'Français',
  de: 'Deutsch', it: 'Italiano', eu: 'Euskara', gl: 'Galego', zh: '中文',
  vi: 'Tiếng Việt', ja: '日本語', ko: '한국어', ru: 'Русский',
  ro: 'Română', ar: 'العربية', tr: 'Türkçe', pl: 'Polski',
  ms: 'Bahasa Melayu', ta: 'தமிழ்',
};
const STORAGE_KEY = 'alb_locale';

function interpolate(str, params) {
  if (!params || !str) return str || '';
  return String(str).replace(/\{(\w+)\}/g, (_, k) => (params[k] !== undefined ? String(params[k]) : `{${k}}`));
}

function detectBrowserLocale(available) {
  if (typeof navigator === 'undefined') return null;
  const langs = (navigator.languages && navigator.languages.length) ? navigator.languages : [navigator.language].filter(Boolean);
  for (const l of langs) {
    if (!l) continue;
    const lower = l.toLowerCase();
    if (available.includes(lower)) return lower;
    const base = lower.split('-')[0];
    if (available.includes(base)) return base;
  }
  return null;
}

async function detectGeoLocale(available) {
  try {
    const r = await api.get('/geo');
    const loc = r.data?.locale;
    if (loc && available.includes(loc)) return loc;
  } catch {}
  return null;
}

const I18nContext = createContext({
  locale: 'en', setLocale: () => {}, t: (k) => k, available: [], names: {}, ready: false,
});

export function I18nProvider({ children }) {
  const [customDicts, setCustomDicts] = useState({}); // code -> dict
  const [customNames, setCustomNames] = useState({}); // code -> display name
  const [ready, setReady] = useState(false);
  const [locale, setLocaleState] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) || null;
  });

  // Fetch custom translations (and built-in overrides) from backend
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await api.get('/i18n/locales');
        const items = r.data?.items || [];
        const dicts = {};
        const names = {};
        for (const it of items) {
          // Any locale with a translations dict from DB - apply as override/custom
          if (it.translations && typeof it.translations === 'object' && Object.keys(it.translations).length > 0) {
            dicts[it.code] = it.translations;
          }
          if (!it.builtin) {
            names[it.code] = it.name || it.code;
          }
        }
        if (mounted) {
          setCustomDicts(dicts);
          setCustomNames(names);
        }
      } catch {}
      if (mounted) setReady(true);
    })();
    return () => { mounted = false; };
  }, []);

  const allDicts = useMemo(() => {
    const out = { ...BUILTIN };
    for (const [code, dict] of Object.entries(customDicts || {})) {
      out[code] = { ...(BUILTIN[code] || {}), ...(dict || {}) };
    }
    return out;
  }, [customDicts]);
  const available = useMemo(() => Object.keys(allDicts), [allDicts]);
  const names = useMemo(() => ({ ...BUILTIN_NAMES, ...customNames }), [customNames]);

  // Once ready, resolve locale. Priority: stored (user choice) > browser
  // language > geographic location > Spanish (es) default.
  useEffect(() => {
    if (!ready) return;
    if (locale && available.includes(locale)) return;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && available.includes(stored)) { setLocaleState(stored); return; }
    const browser = detectBrowserLocale(available);
    if (browser) { setLocaleState(browser); return; }
    let cancelled = false;
    (async () => {
      const geo = await detectGeoLocale(available);
      if (!cancelled) setLocaleState(geo || 'es');
    })();
    return () => { cancelled = true; };
  }, [ready, available, locale]);

  const effectiveLocale = locale && available.includes(locale) ? locale : 'es';

  const setLocale = useCallback((code) => {
    if (!available.includes(code)) return;
    localStorage.setItem(STORAGE_KEY, code);
    setLocaleState(code);
    try { document.documentElement.lang = code; } catch {}
  }, [available]);

  // sync <html lang>
  useEffect(() => {
    try { document.documentElement.lang = effectiveLocale; } catch {}
  }, [effectiveLocale]);

  const t = useCallback((key, params) => {
    const dict = allDicts[effectiveLocale] || {};
    let val = dict[key];
    if (val === undefined) val = (allDicts.en || en)[key];
    if (val === undefined) val = key;
    return interpolate(val, params);
  }, [allDicts, effectiveLocale]);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get('/i18n/locales');
      const items = r.data?.items || [];
      const dicts = {};
      const names = {};
      for (const it of items) {
        if (it.translations && typeof it.translations === 'object' && Object.keys(it.translations).length > 0) {
          dicts[it.code] = it.translations;
        }
        if (!it.builtin) names[it.code] = it.name || it.code;
      }
      setCustomDicts(dicts);
      setCustomNames(names);
    } catch {}
  }, []);

  return (
    <I18nContext.Provider value={{ locale: effectiveLocale, setLocale, t, available, names, ready, builtin: BUILTIN, refresh }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useT() {
  return useContext(I18nContext).t;
}

export function useI18n() {
  return useContext(I18nContext);
}
